import React, { useEffect, lazy, Suspense } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { AccessProtectedRoute } from "./components/common/AccessProtectedRoute";
import { CustomerProtectedRoute } from "./components/common/CustomerProtectedRoute";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { checkTokenExpiry } from "./services/authService";
import { useAuth } from "./contexts/AuthContext";
import { CustomerAuthProvider } from "./contexts/CustomerAuthContext";
import { AICreditsProvider } from "./contexts/AICreditsContext";
import ErrorBoundary from "./components/common/ErrorBoundary";

// Only import critical components needed for initial render
import Layout from "./components/Layout";
import ScrollToTop from "./components/common/ScollToTop";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import CacheCleaner from "./components/CacheCleaner";

// Prefetch commonly accessed routes after initial load
const prefetchCommonRoutes = () => {
  // Wait for initial render to complete
  if (typeof window !== "undefined") {
    const prefetch = () => {
      // Prefetch likely next routes based on user type
      const token = localStorage.getItem("token");
      if (token) {
        // Logged in users likely to visit these - trigger lazy load
        import(
          /* webpackChunkName: "dashboard", webpackPrefetch: true */ "./components/MainMenu"
        );
        import(
          /* webpackChunkName: "dashboard", webpackPrefetch: true */ "./components/Dashboard"
        );
        import(
          /* webpackChunkName: "orders", webpackPrefetch: true */ "./components/orders/ModernOrderSystem"
        );
      } else {
        // Non-logged users likely to visit these
        import(/* webpackChunkName: "auth" */ "./components/login");
        import(/* webpackChunkName: "auth" */ "./components/Register");
        import("./components/PlansPage");
      }
    };

    // Use requestIdleCallback if available, otherwise setTimeout 
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(prefetch);
    } else {
      setTimeout(prefetch, 1000);
    }
  }
};

// Lazy load all other components
const MainHome = lazy(
  () => import(/* webpackChunkName: "home" */ "./components/main-home"),
);
const Login = lazy(
  () => import(/* webpackChunkName: "auth" */ "./components/login"),
);
const Register = lazy(
  () => import(/* webpackChunkName: "auth" */ "./components/Register"),
);
const ForgotPassword = lazy(
  () => import(/* webpackChunkName: "auth" */ "./components/forgotPassword"),
);
const ResetPassword = lazy(
  () => import(/* webpackChunkName: "auth" */ "./components/ResetPassword"),
);

// Public pages
const HowItWorks = lazy(() => import("./components/HowItWorks"));
const AboutUs = lazy(() => import("./components/AboutUs"));
const Benefits = lazy(() => import("./components/Benefits"));
const PlansPage = lazy(() => import("./components/PlansPage"));
const UpgradePlansPage = lazy(() => import("./components/UpgradePlansPage"));
const DemoVideoPage = lazy(() => import("./components/DemoVideoPage"));
const FAQ = lazy(() => import("./components/faq"));
const Home = lazy(() => import("./components/user/Home"));
const CategoryPage = lazy(() => import("./components/CategoryPage"));
const TrackOrder = lazy(() => import("./components/user/TrackOrder"));
const SupplierOrderView = lazy(
  () => import("./pages/public/SupplierOrderView"),
);

// Policy pages
const TermsAndConditions = lazy(
  () => import("./components/policy/terms-and-conditions"),
);
const PrivacyPolicy = lazy(() => import("./components/policy/privacy-policy"));
const CancellationPolicy = lazy(
  () => import("./components/policy/cancellation-policy"),
);

// Customer-facing (QR system)
const QRLanding = lazy(() => import("./pages/customer/QRLanding"));
const CustomerMenu = lazy(() => import("./pages/customer/CustomerMenu"));
const CustomerProfile = lazy(() => import("./pages/customer/CustomerProfile"));
const CustomerOrderConfirmation = lazy(
  () => import("./pages/customer/CustomerOrderConfirmation"),
);
const CustomerFeedback = lazy(
  () => import("./pages/customer/CustomerFeedback"),
);
const CustomerResetPasswordForm = lazy(
  () => import("./components/customer-auth/CustomerResetPasswordForm").then(m => ({ default: m.CustomerResetPasswordForm })),
);
const TableQRRedirect = lazy(() => import("./components/TableQRRedirect"));
const CusProfile = lazy(() => import("./components/user/CusProfile"));
const Discovery = lazy(() => import("./pages/customer/Discovery"));
const CustomerEvents = lazy(() => import("./pages/customer/CustomerEvents"));
const ZilloutPay = lazy(() => import("./pages/customer/ZilloutPay"));
const SocialFeed = lazy(() => import("./pages/customer/SocialFeed"));
const WriteReview = lazy(() => import("./pages/customer/WriteReview"));
// Removed duplicate LoyaltyHub import
// ...existing code...
const EventManagement = lazy(() => import("./pages/admin/EventManagement"));
const ReservationCheckIn = lazy(
  () =>
    import(/* webpackChunkName: "reservations" */ "./pages/ReservationCheckIn"),
);
const SecurityLogin = lazy(() =>
  import(
    /* webpackChunkName: "security" */ "./pages/security/SecurityLogin"
  ).then((module) => ({ default: module.SecurityLogin })),
);
const SecurityScanner = lazy(() =>
  import(
    /* webpackChunkName: "security" */ "./pages/security/SecurityScanner"
  ).then((module) => ({ default: module.SecurityScanner })),
);

// Captain/Waiter app (barrel import for cleaner code)
const {
  CaptainLogin,
  CaptainDashboard,
  CaptainProfile,
  TableDetails,
  OrderTaking,
  CaptainOrders,
  CaptainMenu,
  CaptainBill,
  CaptainSplitBillWrapper,
  CaptainNotFound,
} = {
  CaptainLogin: lazy(() =>
    import("./pages/captain").then((m) => ({ default: m.CaptainLogin })),
  ),
  CaptainDashboard: lazy(() =>
    import("./pages/captain").then((m) => ({ default: m.CaptainDashboard })),
  ),
  CaptainProfile: lazy(() =>
    import("./pages/captain").then((m) => ({ default: m.CaptainProfile })),
  ),
  TableDetails: lazy(() =>
    import("./pages/captain").then((m) => ({ default: m.TableDetails })),
  ),
  OrderTaking: lazy(() =>
    import("./pages/captain").then((m) => ({ default: m.OrderTaking })),
  ),
  CaptainOrders: lazy(() =>
    import("./pages/captain").then((m) => ({ default: m.CaptainOrders })),
  ),
  CaptainMenu: lazy(() =>
    import("./pages/captain").then((m) => ({ default: m.CaptainMenu })),
  ),
  CaptainBill: lazy(() =>
    import("./pages/captain").then((m) => ({ default: m.CaptainBill })),
  ),
  CaptainSplitBillWrapper: lazy(() =>
    import("./pages/captain").then((m) => ({
      default: m.CaptainSplitBillWrapper,
    })),
  ),
  CaptainNotFound: lazy(() =>
    import("./pages/captain").then((m) => ({ default: m.CaptainNotFound })),
  ),
};

// Admin/Dashboard - Preload critical routes
const MainMenu = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard", webpackPrefetch: true */ "./components/MainMenu"
    ),
);
const Dashboard = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard", webpackPrefetch: true */ "./components/Dashboard"
    ),
);
const AdminProfile = lazy(
  () => import(/* webpackChunkName: "admin" */ "./components/AdminProfile"),
);
const AdminInterface = lazy(
  () => import(/* webpackChunkName: "admin" */ "./components/AdminInterface"),
);
const AdminIndex = lazy(
  () => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminIndex"),
);
const AdminRestaurants = lazy(
  () =>
    import(/* webpackChunkName: "admin" */ "./pages/admin/AdminRestaurants"),
);
const AdminSubscriptions = lazy(
  () =>
    import(/* webpackChunkName: "admin" */ "./pages/admin/AdminSubscriptions"),
);
const AdminPayments = lazy(
  () =>
    import(/* webpackChunkName: "admin" */ "./pages/admin/AdminPayments"),
);
const AdminHackathonRegistrations = lazy(
  () =>
    import(/* webpackChunkName: "admin" */ "./pages/admin/AdminHackathonRegistrations"),
);
const BillingHistoryPage = lazy(
  () => import("./pages/BillingHistoryPage"),
);
const AdminUsers = lazy(
  () => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminUsers"),
);
const AdminDemoRequests = lazy(
  () =>
    import(/* webpackChunkName: "admin" */ "./pages/admin/AdminDemoRequests"),
);
const HopeFeaturesAdmin = lazy(
  () => import(/* webpackChunkName: "admin" */ "./pages/admin/HopeFeaturesAdmin")
);
const AdminGlobalIngredients = lazy(
  () =>
    import(
      /* webpackChunkName: "admin" */ "./pages/admin/AdminGlobalIngredients"
    ),
);
const AdminGlobalRecipes = lazy(
  () =>
    import(/* webpackChunkName: "admin" */ "./pages/admin/AdminGlobalRecipes"),
);
const AdminGlobalImages = lazy(
  () =>
    import(/* webpackChunkName: "admin" */ "./pages/admin/AdminGlobalImages"),
);
const AdminPlans = lazy(
  () => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminPlans"),
);
const AdminSettings = lazy(
  () => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminSettings"),
);
const AdminAnalytics = lazy(
  () => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminAnalytics"),
);
const WhatsAppAdminConfig = lazy(
  () =>
    import(/* webpackChunkName: "admin" */ "./pages/admin/WhatsAppAdminConfig"),
);
const AdminWhatsAppTemplateSuggestions = lazy(
  () =>
    import(
      /* webpackChunkName: "admin" */ "./pages/admin/AdminWhatsAppTemplateSuggestions"
    ),
);
const AdminWhatsAppRevenue = lazy(
  () =>
    import(
      /* webpackChunkName: "admin" */ "./pages/admin/AdminWhatsAppRevenue"
    ),
);
const AdminWhatsAppPricing = lazy(
  () =>
    import(
      /* webpackChunkName: "admin" */ "./pages/admin/AdminWhatsAppPricing"
    ),
);
const AdminRestaurantWhatsApp = lazy(
  () =>
    import(
      /* webpackChunkName: "admin" */ "./pages/admin/AdminRestaurantWhatsApp"
    ),
);
const AdminAiUsage = lazy(
  () => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminAiUsage"),
);
const AdminAiCreditPacks = lazy(
  () => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminAiCreditPacks"),
);
const AdminInternalStaff = lazy(
  () => import(/* webpackChunkName: "admin" */ "./components/admin/WillovateInternalStaff"),
);

// Restaurant Management
const RestaurantProfile = lazy(
  () => import("./pages/RestaurantProfileSwitcher"),
);
const EditRestaurantProfile = lazy(
  () => import("./pages/EditRestaurantProfile"),
);
const RestaurantOnboarding = lazy(
  () => import("./pages/RestaurantOnboarding"),
);
const PendingApproval = lazy(
  () => import("./pages/PendingApproval"),
);
const TableManagement = lazy(() => import("./components/TableManagement"));
const ReservationManagement = lazy(
  () => import("./pages/ReservationManagement"),
);
const TableQRGenerator = lazy(() => import("./components/TableQRGenerator"));
const KitchenDisplayScreen = lazy(() =>
  import("./components/KitchenDisplayScreen").then((module) => ({
    default: module.KitchenDisplayScreen,
  })),
);
const ManageLocationsPage = lazy(() => import("./pages/ManageLocationsPage"));
const SecureQRDemo = lazy(() => import("./pages/admin/SecureQRDemo"));
const BookTable = lazy(() => import("./pages/BookTable"));
const ThemeCustomization = lazy(() => import("./pages/ThemeCustomization"));

// Menu & Items
const AddEditDish = lazy(() => import("./components/add-edit-dish"));
const MenuManagementPage = lazy(
  () => import("./components/MenuManagementPage"),
);
const IntelligentMenuDashboard = lazy(
  () => import("./components/intelligent-menu/IntelligentMenuDashboard"),
);

// Orders - High priority, prefetch
const ModernOrderSystem = lazy(
  () =>
    import(
      /* webpackChunkName: "orders", webpackPrefetch: true */ "./components/orders/ModernOrderSystem"
    ),
);
const OrderManagementDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: "orders" */ "./components/orders/OrderManagementDashboard"
    ),
);
const ModernOrderDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "orders" */ "./components/orders/ModernOrderDetails"
    ),
);

// Billing
const BillPrintExample = lazy(() => import("./components/BillPrintExample"));
const SplitBillPage = lazy(() => import("./components/SplitBillPage"));

// Inventory
const InventoryDashboard = lazy(
  () => import("./components/inventory/InventoryDashboard"),
);
const InventoryManagementPage = lazy(
  () => import("./components/inventory/InventoryManagementPage"),
);
const AddEditIngredient = lazy(
  () => import("./components/inventory/AddEditIngredient"),
);
const InventoryPurchase = lazy(
  () => import("./components/inventory/InventoryPurchase"),
);
const StockMovementsPage = lazy(
  () => import("./components/stock-movements/StockMovementsPage"),
);
const IngredientStockMovementPage = lazy(
  () => import("./components/stock-movements/IngredientStockMovementPage"),
);
const InventoryAlertsPage = lazy(
  () => import("./components/inventory-alerts/InventoryAlertsPage"),
);
const InventoryConsumption = lazy(
  () => import("./components/inventory/InventoryConsumption"),
);
const InventorySuppliers = lazy(
  () => import("./components/inventory/InventorySuppliers"),
);
const InventoryReports = lazy(
  () => import("./components/inventory/InventoryReports"),
);
const StorageLocationsPage = lazy(
  () => import("./components/inventory/StorageLocationsPage"),
);

// Reports - Group into single chunk
const ReportsMainDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/ReportsMainDashboard"
    ),
);
const DailySalesReport = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/DailySalesReport"
    ),
);
const WMYReports = lazy(
  () =>
    import(/* webpackChunkName: "reports" */ "./components/reports/WMYReports"),
);
const TaxGSTReport = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/TaxGSTReport"
    ),
);
const ItemSalesReport = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/ItemSalesReport"
    ),
);
const CategorySalesReport = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/CategorySalesReport"
    ),
);

const CancelledOrdersReport = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/CancelledOrdersReport"
    ),
);
const ExpenseReport = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/ExpenseReport"
    ),
);
const WastageReport = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/WastageReport"
    ),
);
const ProfitLossReport = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/ProfitLossReport"
    ),
);
const RewardCatalogReport = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/RewardCatalogReport"
    ),
);
const CustomerVisit = lazy(
  () =>
    import(
      /* webpackChunkName: "reports" */ "./components/reports/CustomerVisit"
    ),
);

// HR Management
const HRMainDashboard = lazy(() => import("./components/hr/HRMainDashboard"));
const PayrollAttendanceManagement = lazy(
  () => import("./components/hr/PayrollAttendanceManagement"),
);
const AuditLogsPage = lazy(() => import("./pages/AuditLogsPage"));
const ManualAttendancePayroll = lazy(
  () => import("./components/hr/ManualAttendancePayroll"),
);
const UserManagement = lazy(() => import("./components/admin/UserManagement"));

// Loyalty & Promotions
const LoyaltyHub = lazy(() => import("./components/loyalty/LoyaltyHub"));
const PromotionsDashboard = lazy(
  () => import("./components/promotions/PromotionsDashboard"),
);

// WhatsApp Integration
const WhatsAppManagement = lazy(() => import("./pages/WhatsAppManagement"));
const WhatsAppTemplates = lazy(() => import("./pages/WhatsAppTemplates"));
const WhatsAppTemplateSuggestions = lazy(
  () => import("./pages/WhatsAppTemplateSuggestions"),
);
const WhatsAppRecharge = lazy(() => import("./pages/WhatsAppRecharge"));
const WhatsAppBillingDashboard = lazy(
  () => import("./pages/WhatsAppBillingDashboard"),
);
const RbacAdminDashboard = lazy(
  () => import("./pages/admin/RbacAdminDashboard"),
);

// Payment & Plans
const PaymentButton = lazy(() => import("./components/payment"));
const PlanCheckoutPage = lazy(() => import("./components/PlanCheckoutPage"));
const PaymentWaitingPage = lazy(
  () => import("./components/PaymentWaitingPage"),
);
const SuccessPage = lazy(() => import("./components/SuccessPage"));
const PaymentStatus = lazy(() => import("./pages/PaymentStatus"));
const PhonePePaymentStatus = lazy(() => import("./pages/PhonePePaymentStatus"));
const PaymentTestPage = lazy(() => import("./components/PaymentTestPage"));
const AICreditsWallet = lazy(() => import("./components/AICreditsWallet"));

// Settings
const Settings = lazy(() => import("./pages/Settings"));

// Error pages
const Unauthorized = lazy(() => import("./pages/unauthorized"));
const UpgradeRequired = lazy(() => import("./pages/UpgradeRequired"));
const NoAccessPage = lazy(() => import("./components/NoAccesspage"));

// Development tools
// const FeatureAccessDebugger = lazy(() => import("./components/common/FeatureAccessDebugger").then(module => ({ default: module.FeatureAccessDebugger })));
const RouteTest = lazy(() => import("./pages/RouteTest"));

// Loading screen component - shows while auth is initializing
const LoadingScreen: React.FC = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#f5f5f5",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <div
        style={{
          width: "50px",
          height: "50px",
          border: "5px solid #e0e0e0",
          borderTop: "5px solid #004874ff",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }}
      />
      <p style={{ fontSize: "18px", color: "#555" }}>Loading</p>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, isInitialized, subscription } = useAuth();
  const hidePWAInstallPrompt = location.pathname.startsWith(
    "/restaurant-profile/view/",
  );

  useEffect(() => {
    // Prefetch common routes after component mounts
    prefetchCommonRoutes();

    // Wait for auth initialization before proceeding
    if (!isInitialized) {
      return;
    }

    const token = localStorage.getItem("token");
    // Use reactive subscription from auth context (updated by background refresh
    // after admin approves a plan). Fall back to localStorage for the very first
    // render before SessionManager has initialised.
    const planStatus = subscription?.isActive
      ? "active"
      : (subscription?.status?.toLowerCase() ?? localStorage.getItem("plan_status"));
    const urlParams = new URLSearchParams(window.location.search);
    const isPWALaunch = urlParams.get("source") === "pwa";

    // Check token validity on app load
    if (token) {
      const isTokenValid = checkTokenExpiry();
      if (!isTokenValid) {
        // Token expired - clear storage
        localStorage.clear();
        // Don't redirect here - let them stay on current page
        // If they're on a protected route, ProtectedRoute will handle redirect
        return;
      }
    }

    // PWA Launch: Smart redirect based on user type and logout state
    if (isPWALaunch && location.pathname === "/") {
      const userType = localStorage.getItem("userType"); // 'captain' or 'admin'
      const forceLoginPage = localStorage.getItem("forceLoginPage"); // Set on logout
      const lastPath = localStorage.getItem("lastVisitedPath");
      const captainToken = localStorage.getItem("captainToken");

      // If user logged out via PWA, redirect to their login page
      if (forceLoginPage === "captain") {
        localStorage.removeItem("forceLoginPage");
        navigate("/captain/login", { replace: true });
        return;
      }
      if (forceLoginPage === "admin") {
        localStorage.removeItem("forceLoginPage");
        navigate("/login", { replace: true });
        return;
      }

      // Captain user - check if logged in, then go to dashboard
      if (userType === "captain") {
        if (captainToken) {
          navigate("/captain/dashboard", { replace: true });
        } else {
          navigate("/captain/login", { replace: true });
        }
        return;
      }

      // Admin/Owner/Manager - restore last page or go to main
      if (token) {
        if (
          lastPath &&
          lastPath !== "/" &&
          lastPath !== "/login" &&
          lastPath !== "/register"
        ) {
          navigate(lastPath, { replace: true });
        } else if (planStatus === "active") {
          navigate("/main", { replace: true });
        } else {
          navigate("/plan-section", { replace: true });
        }
        return;
      }
    }

    // NON-PWA: Normal browser navigation
    // Only redirect logged-in users away from homepage to their dashboard
    // If no token, let them see the public marketing page
    if (token && planStatus === "active" && location.pathname === "/") {
      navigate("/main", { replace: true });
    } else if (token && planStatus !== "active" && location.pathname === "/") {
      navigate("/plan-section", { replace: true });
    }
    // If no token and on "/", do nothing - show MainHome (marketing page)
  }, [location.pathname, navigate, isInitialized, subscription]);

  // Track last visited path for PWA restore
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userType = localStorage.getItem("userType");

    // Only track path for non-captain users (admins/owners/managers)
    if (
      token &&
      userType !== "captain" &&
      location.pathname !== "/login" &&
      location.pathname !== "/register"
    ) {
      localStorage.setItem("lastVisitedPath", location.pathname);
    }
  }, [location.pathname]);

  // Show loading screen while auth is initializing
  if (!isInitialized || loading) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <CustomerAuthProvider>
        <AICreditsProvider>
        <ScrollToTop />
        <ToastContainer position="bottom-left" autoClose={3000} />

        {/* PWA Install Prompt - hidden on public restaurant profile pages */}
        {!hidePWAInstallPrompt && <PWAInstallPrompt />}

        {/* Cache Cleaner - Dev Tools and Reload Loop Detection */}
        <CacheCleaner />

        {/* Console-based debugging - DISABLED TO PREVENT STORAGE SIDE EFFECTS
      {(() => {
        const features = localStorage.getItem("availableFeatures");
        const role = localStorage.getItem("role");
        const plan = localStorage.getItem("plan");

        if (process.env.NODE_ENV === 'development') {
           // benign logging only
        }

        return null;
      })()}
      */}

        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Layout />}>
              <Route path="/" element={<MainHome />} />
              <Route path="/explore" element={<Discovery />} />
              <Route path="/events" element={<CustomerEvents />} />
              <Route path="/pay/:orderId" element={<ZilloutPay />} />
              <Route path="/social" element={<SocialFeed />} />
              <Route path="/rewards" element={<LoyaltyHub />} />
              <Route
                path="/review/:restaurantId/:restaurantName"
                element={<WriteReview />}
              />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/benefits" element={<Benefits />} />
              <Route path="/plan-section" element={<PlansPage />} />
              <Route path="/plan-upgrade" element={<UpgradePlansPage />} />
              <Route path="/demo" element={<DemoVideoPage />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/restaurant" element={<Home />} />
              <Route path="/restaurant/:name" element={<CategoryPage />} />
              <Route path="/track-order" element={<TrackOrder />} />

              <Route path="/t&c" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/cancellation-policy"
                element={<CancellationPolicy />}
              />

              {/* QR Code redirect - Redirect old format to new customer system */}
              <Route
                path="/table/:restaurantId/:tableNumber"
                element={<TableQRRedirect />}
              />
            </Route>

            <Route path="/book-table" element={<BookTable />} />
            <Route path="/payment-waiting" element={<PaymentWaitingPage />} />
            <Route path="/payment-status" element={<PaymentStatus />} />
            <Route path="/payment/status" element={<PhonePePaymentStatus />} />
            <Route path="/phonepe/status" element={<PhonePePaymentStatus />} />

            <Route path="/" element={<Layout />}>
              {/* Customer Protected Route (keep here with public and customer routes) */}
              <Route
                path="/cus-profile"
                element={
                  <CustomerProtectedRoute>
                    <CusProfile />
                  </CustomerProtectedRoute>
                }
              />
            </Route>

            {/* Customer-facing routes (standalone, no layout) */}
            {/* Supplier Public Order View */}
            <Route
              path="/supplier/orders/:token"
              element={<SupplierOrderView />}
            />

            <Route path="/qr/:token" element={<QRLanding />} />
            {/* Secure customer menu route with encrypted token */}
            <Route
              path="/customer/menu/secure/:token"
              element={<CustomerMenu />}
            />
            {/* Customer profile route */}
            <Route path="/customer/profile" element={<CustomerProfile />} />
            {/* Secure order confirmation route with encrypted token */}
            <Route
              path="/customer/order-confirmation/secure/:token"
              element={<CustomerOrderConfirmation />}
            />
            {/* Legacy routes for backward compatibility - these should be phased out */}
            <Route
              path="/customer/menu/:restaurantId"
              element={<CustomerMenu />}
            />
            <Route
              path="/customer/menu/:restaurantId/:tableId"
              element={<CustomerMenu />}
            />
            <Route
              path="/customer/order-confirmation/:restaurantId/:orderId"
              element={<CustomerOrderConfirmation />}
            />
            <Route path="/customer/feedback" element={<CustomerFeedback />} />
            <Route path="/customer/reset-password" element={<CustomerResetPasswordForm />} />

            {/* Route test page */}
            <Route path="/test-routes" element={<RouteTest />} />

            {/* Captain/Waiter App routes (standalone, mobile-optimized) */}
            <Route path="/captain/login" element={<CaptainLogin />} />
            <Route path="/captain/dashboard" element={<CaptainDashboard />} />
            <Route path="/captain/profile" element={<CaptainProfile />} />
            <Route path="/captain/orders" element={<CaptainOrders />} />
            <Route path="/captain/menu" element={<CaptainMenu />} />
            <Route path="/captain/table/:tableId" element={<TableDetails />} />
            <Route path="/captain/order/:tableId" element={<OrderTaking />} />
            <Route path="/captain/order/:orderId" element={<CaptainBill />} />
            <Route path="/captain/bill/:tableId" element={<CaptainBill />} />
            <Route
              path="/captain/split-bill/:tableId"
              element={<CaptainSplitBillWrapper />}
            />
            {/* Captain 404 - Catch all invalid captain routes */}
            <Route path="/captain/*" element={<CaptainNotFound />} />

            {/* Security App routes (standalone, mobile-optimized) */}
            <Route path="/security/login" element={<SecurityLogin />} />
            <Route path="/security/scanner" element={<SecurityScanner />} />

            {/* Public Restaurant Profile - Standalone (no Willovate branding, acts as restaurant's own website) */}
            {/* Secure route with encrypted token */}
            <Route
              path="/restaurant-profile/view/:token"
              element={<RestaurantProfile />}
            />
            {/* Legacy route with direct ID for backward compatibility */}
            <Route
              path="/restaurant-profile/:restaurantId"
              element={<RestaurantProfile />}
            />

            {/* Auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Onboarding routes — standalone, no sidebar/navbar */}
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/restaurant-onboarding" element={<RestaurantOnboarding />} />

            {/* Protected routes */}
            <Route path="/" element={<Layout />}>
              <Route
                path="/tables"
                element={
                  <AccessProtectedRoute requireModule="TABLES">
                    <TableManagement />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/reservation-management"
                element={
                  <AccessProtectedRoute requireModule="TABLES">
                    <ReservationManagement />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/reservation/check-in/:code"
                element={
                  <AccessProtectedRoute requireModule="RESERVATION" featureName="Reservations">
                    <ReservationCheckIn />
                  </AccessProtectedRoute>
                }
              />

              {/* Scanner Route (No code pre-filled) */}
              <Route
                path="/reservation/check-in"
                element={
                  <AccessProtectedRoute requireModule="RESERVATION" featureName="Reservations">
                    <ReservationCheckIn />
                  </AccessProtectedRoute>
                }
              />

              {/* Events & Nightlife Management */}
              <Route path="/event-management" element={<EventManagement />} />

              <Route
                path="/kitchen-display"
                element={
                  <AccessProtectedRoute requireModule="KITCHEN_DISPLAY" featureName="Kitchen Display">
                    <KitchenDisplayScreen />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/qr-generator"
                element={
                  <AccessProtectedRoute requireModule="QR_FLOW" featureName="QR Generator">
                    <TableQRGenerator />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/table-locations"
                element={
                  <AccessProtectedRoute requireModule="TABLES">
                    <ManageLocationsPage />
                  </AccessProtectedRoute>
                }
              />

              <Route path="/payment-test" element={<PaymentTestPage />} />
              <Route path="/qr-demo" element={<SecureQRDemo />} />
              <Route
                path="/main"
                element={
                  <AccessProtectedRoute requireModule="DASHBOARD">
                    <MainMenu />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/admin-profile"
                element={
                  <AccessProtectedRoute>
                    <AdminProfile />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/billing-history"
                element={
                  <AccessProtectedRoute>
                    <BillingHistoryPage />
                  </AccessProtectedRoute>
                }
              />

              {/* Internal Restaurant Profile routes (in Layout) */}
              <Route
                path="/restaurant-profile"
                element={
                  <AccessProtectedRoute requirePermission="RESTAURANT_FULL_ACCESS">
                    <RestaurantProfile />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/theme-studio"
                element={
                  <AccessProtectedRoute requirePermission="RESTAURANT_FULL_ACCESS" featureName="Theme Studio">
                    <ThemeCustomization />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/restaurant-profile/create"
                element={
                  <AccessProtectedRoute requirePermission="RESTAURANT_FULL_ACCESS" featureName="Restaurant Profile">
                    <EditRestaurantProfile />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/restaurant-profile/:restaurantId/edit"
                element={
                  <AccessProtectedRoute requirePermission="RESTAURANT_FULL_ACCESS" featureName="Restaurant Profile">
                    <EditRestaurantProfile />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/add-edit-dish"
                element={
                  <AccessProtectedRoute requirePermission="MENU_FULL_ACCESS" featureName="Menu Management">
                    <AddEditDish />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/inventory"
                element={
                  <AccessProtectedRoute requireModule="INVENTORY">
                    <InventoryDashboard />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/inventory/management"
                element={
                  <AccessProtectedRoute requirePermission="EDIT_INGREDIENT_ACCESS" featureName="Ingredient Management">
                    <InventoryManagementPage />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/inventory/add"
                element={
                  <AccessProtectedRoute requirePermission="EDIT_INGREDIENT_ACCESS" featureName="Add Ingredient">
                    <AddEditIngredient />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/inventory/edit/:id"
                element={
                  <AccessProtectedRoute requirePermission="EDIT_INGREDIENT_ACCESS" featureName="Edit Ingredient">
                    <AddEditIngredient />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/inventory/purchase"
                element={
                  <AccessProtectedRoute requireModule="INVENTORY" requirePermission="PURCHASE_ORDER_ACCESS" featureName="Purchase Orders">
                    <InventoryPurchase />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/inventory/stock-movements"
                element={
                  <AccessProtectedRoute requireModule="INVENTORY">
                    <StockMovementsPage />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/inventory/stock-movement/:ingredientId"
                element={
                  <AccessProtectedRoute requireModule="INVENTORY">
                    <IngredientStockMovementPage />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/inventory/alerts"
                element={
                  <AccessProtectedRoute requireModule="INVENTORY">
                    <InventoryAlertsPage />
                  </AccessProtectedRoute>
                }
              />

              {/* COMMENTED OUT: RECIPES module no longer available in backend */}
              <Route
                path="/inventory/consumption"
                element={
                  <AccessProtectedRoute requireModule="INVENTORY">
                    <InventoryConsumption />
                  </AccessProtectedRoute>
                }
              /> 

              {/* COMMENTED OUT: SUPPLIERS module no longer available in backend */}
              <Route
                path="/inventory/suppliers"
                element={
                  <AccessProtectedRoute requireModule="INVENTORY">
                    <InventorySuppliers />
                  </AccessProtectedRoute>
                }
              /> 

              <Route
                path="/inventory/reports"
                element={
                  <AccessProtectedRoute requirePermission="INVENTORY_REPORTS_ACCESS" featureName="Inventory Reports">
                    <InventoryReports />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/inventory/storage-locations"
                element={
                  <AccessProtectedRoute requireModule="INVENTORY">
                    <StorageLocationsPage />
                  </AccessProtectedRoute>
                }
              />

              {/* Intelligent Menu - Requires INTELLIGENT_MENU module access */}
              <Route
                path="/intelligent-menu"
                element={
                  <AccessProtectedRoute requireModule="INTELLIGENT_MENU">
                    <IntelligentMenuDashboard />
                  </AccessProtectedRoute>
                }
              />

              {/* Menu Management - Basic Menu Access */}
              <Route
                path="/menu-management"
                element={
                  <AccessProtectedRoute requireModule="MENU_BASIC">
                    <MenuManagementPage />
                  </AccessProtectedRoute>
                }
              />

              <Route path="/payment" element={<PaymentButton />} />
              <Route
                path="/ai-credits"
                element={
                  <AccessProtectedRoute requireModule="AI_CREDITS" featureName="AI Credits">
                    <AICreditsWallet />
                  </AccessProtectedRoute>
                }
              />

              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/no-access" element={<NoAccessPage />} />
              <Route path="/upgrade-required" element={<UpgradeRequired />} />

              <Route path="/plans/checkout" element={<PlanCheckoutPage />} />

              <Route path="/sucesspage" element={<SuccessPage />} />

              <Route
                path="/order-details/:orderId"
                element={<ModernOrderDetails />}
              />

              <Route path="/split-bill/:orderId" element={<SplitBillPage />} />

              <Route
                path="/pos"
                element={
                  <AccessProtectedRoute requireModule="POS">
                    <ModernOrderSystem />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/order-management"
                element={
                  <AccessProtectedRoute requireModule="ORDERS">
                    <OrderManagementDashboard />
                  </AccessProtectedRoute>
                }
              />

              <Route path="/bill/direct" element={<CaptainBill />} />

              <Route path="/bill/:orderId" element={<CaptainBill />} />

              <Route
                path="/bill-print-example"
                element={<BillPrintExample />}
              />

              <Route
                path="/dashboard"
                element={
                  <AccessProtectedRoute requireModule="ANALYTICS" requirePermission="FINANCIAL_ANALYTICS">
                    <Dashboard />
                  </AccessProtectedRoute>
                }
              />

              {/* Reports Routes */}
              <Route
                path="/reports"
                element={
                  <AccessProtectedRoute requireModule="REPORT" featureName="Reports">
                    <ReportsMainDashboard />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/reports/daily-sales"
                element={
                  <AccessProtectedRoute requirePermission="SALES_REPORTS" featureName="Daily Sales Report">
                    <DailySalesReport />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/reports/weekly-monthly-summary"
                element={
                  <AccessProtectedRoute requirePermission="SALES_REPORTS" featureName="Weekly / Monthly Summary">
                    <WMYReports />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/reports/tax-gst"
                element={
                  <AccessProtectedRoute requirePermission="FINANCIAL_REPORTS" featureName="Tax & GST Report">
                    <TaxGSTReport />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/reports/item-sales"
                element={
                  <AccessProtectedRoute requirePermission="SALES_REPORTS" featureName="Item Sales Report">
                    <ItemSalesReport />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/reports/category-sales"
                element={
                  <AccessProtectedRoute requirePermission="SALES_REPORTS" featureName="Category Sales Report">
                    <CategorySalesReport />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/reports/cancelled-orders"
                element={
                  <AccessProtectedRoute requirePermission="SALES_REPORTS" featureName="Cancelled Orders Report">
                    <CancelledOrdersReport />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/reports/pnl-summary"
                element={
                  <AccessProtectedRoute requirePermission="FINANCIAL_REPORTS" featureName="P&L Summary">
                    <ProfitLossReport />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/reports/expense-report"
                element={
                  <AccessProtectedRoute requirePermission="FINANCIAL_REPORTS" featureName="Expense Report">
                    <ExpenseReport />
                  </AccessProtectedRoute>
                }
              />

              {/* COMMENTED OUT: WASTAGE module no longer available in backend */}
              {/* <Route
                path="/reports/wastage-report"
                element={
                  // <AccessProtectedRoute requireModule="WASTAGE">
                  <WastageReport />
                  // </AccessProtectedRoute>
                }
              /> */}

              <Route
                path="/reports/reward-catalog"
                element={
                  <AccessProtectedRoute requirePermission="LOYALTY_MANUAL_ADJUST" featureName="Reward Catalog Report">
                    <RewardCatalogReport />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/reports/customer-visit"
                element={
                  <AccessProtectedRoute requirePermission="CUSTOMER_REPORTS" featureName="Customer Visit Report">
                    <CustomerVisit />
                  </AccessProtectedRoute>
                }
              />

              {/* Loyalty Program Routes */}
              <Route
                path="/loyalty"
                element={
                  <AccessProtectedRoute requireModule="LOYALTY">
                    <LoyaltyHub />
                  </AccessProtectedRoute>
                }
              />

              {/* Promotional Campaigns Routes */}
              <Route
                path="/promotions"
                element={
                  <AccessProtectedRoute requireModule="MARKETING">
                    <PromotionsDashboard />
                  </AccessProtectedRoute>
                }
              />

              {/* WhatsApp Business Integration */}
              <Route
                path="/whatsapp-management"
                element={
                  <AccessProtectedRoute requireModule="WHATSAPP">
                    <WhatsAppManagement />
                  </AccessProtectedRoute>
                }
              />

              {/* WhatsApp Templates */}
              <Route
                path="/whatsapp-templates"
                element={
                  <AccessProtectedRoute requireModule="WHATSAPP">
                    <WhatsAppTemplates />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/whatsapp-suggestions"
                element={
                  <AccessProtectedRoute requireModule="WHATSAPP">
                    <WhatsAppTemplateSuggestions />
                  </AccessProtectedRoute>
                }
              />

              {/* WhatsApp Wallet Recharge */}
              <Route
                path="/whatsapp-recharge"
                element={
                  <AccessProtectedRoute requireModule="WHATSAPP">
                    <WhatsAppRecharge />
                  </AccessProtectedRoute>
                }
              />

              {/* WhatsApp Billing Dashboard (Restaurant Owners) */}
              <Route
                path="/whatsapp-billing"
                element={
                  <AccessProtectedRoute requireModule="WHATSAPP">
                    <WhatsAppBillingDashboard />
                  </AccessProtectedRoute>
                }
              />

              {/* WhatsApp Admin Configuration (Willovate Admins Only) */}
              <Route
                path="/admin/whatsapp-config"
                element={<WhatsAppAdminConfig />}
              />

              {/* WhatsApp Template Suggestions Admin */}
              <Route
                path="/admin/whatsapp-template-suggestions"
                element={<AdminWhatsAppTemplateSuggestions />}
              />

              {/* WhatsApp Revenue Dashboard (Willovate Admins Only) */}
              <Route
                path="/admin/whatsapp-revenue"
                element={<AdminWhatsAppRevenue />}
              />

              {/* WhatsApp Pricing Configuration (Willovate Admins Only) */}
              <Route
                path="/admin/whatsapp-pricing"
                element={<AdminWhatsAppPricing />}
              />
              {/* Restaurant WhatsApp Credentials Management (Willovate Admins Only) */}
              <Route
                path="/admin/restaurant-whatsapp-credentials"
                element={<AdminRestaurantWhatsApp />}
              />

              {/* RBAC Administration (Willovate Admins Only) */}
              <Route
                path="/admin/rbac-config"
                element={<RbacAdminDashboard />}
              />

              {/* Audit Logs */}
              <Route
                path="/audit-logs"
                element={
                  <AccessProtectedRoute requireModule="AUDIT">
                    <AuditLogsPage />
                  </AccessProtectedRoute>
                }
              />

              {/* HR Routes */}
              <Route
                path="/hr"
                element={
                  <AccessProtectedRoute requireModule="HR">
                    <HRMainDashboard />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/hr/payroll-attendance"
                element={
                  <AccessProtectedRoute requireAnyModule={["PAYROLL", "HR"]}>
                    <PayrollAttendanceManagement />
                  </AccessProtectedRoute>
                }
              />
              <Route
                path="/hr/manual-attendance"
                element={
                  <AccessProtectedRoute requireModule="HR">
                    <ManualAttendancePayroll />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <AccessProtectedRoute requireModule="SETTINGS">
                    <Settings />
                  </AccessProtectedRoute>
                }
              />

              <Route
                path="/user-management"
                element={
                  <AccessProtectedRoute requireModule="USERS">
                    <UserManagement />
                  </AccessProtectedRoute>
                }
              />

              <Route path="/admin" element={<AdminInterface />} />
            </Route>

            {/* Willovate SystemAdmin routes - with nested routing */}
            <Route path="/willovate-admin">
              <Route index element={<AdminIndex />} />
              <Route path="restaurants" element={<AdminRestaurants />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="hackathon" element={<AdminHackathonRegistrations />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="hope-features" element={<HopeFeaturesAdmin />} />
              <Route path="demo-requests" element={<AdminDemoRequests />} />
              <Route
                path="global-ingredients"
                element={<AdminGlobalIngredients />}
              />
              <Route path="global-recipes" element={<AdminGlobalRecipes />} />
              <Route path="global-images" element={<AdminGlobalImages />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="whatsapp-admin" element={<WhatsAppAdminConfig />} />
              <Route
                path="whatsapp-template-suggestions"
                element={<AdminWhatsAppTemplateSuggestions />}
              />
              <Route
                path="whatsapp-revenue"
                element={<AdminWhatsAppRevenue />}
              />
              <Route
                path="whatsapp-pricing"
                element={<AdminWhatsAppPricing />}
              />
              <Route
                path="restaurant-whatsapp-credentials"
                element={<AdminRestaurantWhatsApp />}
              />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="ai-usage" element={<AdminAiUsage />} />
              <Route path="ai-credit-packs" element={<AdminAiCreditPacks />} />
              <Route path="internal-staff" element={<AdminInternalStaff />} />
            </Route>
          </Routes>
        </Suspense>
        </AICreditsProvider>
      </CustomerAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
